import React, { Component } from 'react';
import { Modal, Row, Col, OverlayTrigger, Tooltip } from 'react-bootstrap';
import Scheduler, { DATE_FORMAT, SchedulerData } from '../Roster/RosterScheduler';
import { CellUnits, ViewTypes } from '../../../components/Scheduler';
import { inject, observer } from 'mobx-react';
import moment from 'moment';

// Components
import applicationRouter from '../../../hoc/applicationRouter';
import withLocalization from '../../../hoc/withLocalization';
import DatePicker from '../../../components/DatePicker/DatePicker';
import { ProjectsWidget } from '../../../components/GenericForm/Widgets/ProjectsWidget';
import { TasksWidget } from '../../../components/GenericForm/Widgets/TasksWidget';
import { CurrentUserGroupsWidget } from '../../../components/GenericForm/Widgets/UserGroupWidget';
import { AfterBulkCreateFailedResponseModalBody, BulkShiftSuccessSummary } from './AfterCopyResponse.js';

// Elements
import Button from '~/components/CustomButton/CustomButton.jsx';
import LoadingSpinner from '~/elements/LoadingSpinner';
import CustomMultiSelect from '../../../elements/CustomMultiSelect';
import Select from '../../../elements/Select';
import Checkbox from '../../../components/CustomCheckbox/CustomCheckbox';
import AutoTimeRecordingDays from '~/components/RosterShiftOptions/AutoTimeRecordingDays';
import RosterOverrideSettings from '~/components/RosterShiftOptions/RosterOverrideSettings';

// Assets
import CloseModalIcon from '../../../assets/img/CloseApproveModalIcon.svg';

// Utils
import { minutesToTimeInput } from '../../../utils/timeCalc';
import { longDateFormat, shortDateFormat } from '../../../utils/dateFormat';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const generateEventsFromRosterTemplates = (rosterTemplates) => {
    if (!Array.isArray(rosterTemplates)) return [];

    return rosterTemplates.flatMap((template) => {
        const { shift_data = {}, user_id } = template;
        const shiftDetails = template?.Roster_Template?.rules?.shiftDetails || {};
        const events = [];

        Object.entries(shift_data).forEach(([date, { shiftId }]) => {
            const shift = Object.values(shiftDetails).find((s) => s.id === shiftId);
            if (!shift || shift.start_time == null || shift.end_time == null) return;

            const formattedDate = moment(date).format('YYYY-MM-DD');
            const start = `${formattedDate} ${minutesToTimeInput(shift.start_time, false)}:00`;
            const end = `${formattedDate} ${minutesToTimeInput(shift.end_time, false)}:00`;

            events.push({
                id: `${user_id}-${shiftId}-${moment(date).format('dddd').toLowerCase()}`,
                resourceId: user_id,
                title: shift.shift_code,
                start,
                end,
                startTime: shift.start_time,
                endTime: shift.end_time,
                for_date: date,
                shiftId,
                showPopover: true,
            });
        });

        return events;
    });
};

class RosterShiftsModal extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isLoading: true,
            start_date: new Date().toString(),
            end_date: new Date().toString(),
            project_id: null,
            task_id: null,
            user_group: null,
            usersOptions: [],
            userOptionsFilter: [],
            selectedUsers: [],
            publishedTemplates: [],
            resources: [],
            taskMembers: [],
            events: [],
            schedulerData: null,
            width: 0,
            shiftsFailedToCreate: null,
            shiftsCreatedSuccessfully: null,
            departmentOptions: [],
            department: null,
            templateOptions: null,
            selectedTemplate: null,
            autoTimesheetDays: { mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false },
            overrideShift: false,
            overrideAutoTimeRecording: false,
        };
        this.scheduleRef = React.createRef();
    }

    componentDidMount() {
        this.loadData();
        window.addEventListener('resize', this.updateWidth);
    }

    componentDidUpdate(_, prevState) {
        if (prevState.isLoading && !this.state.isLoading) {
            this.updateWidth();
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.updateWidth);
    }

    updateWidth = () => {
        if (this.scheduleRef.current) {
            this.setState({ width: this.scheduleRef.current.offsetWidth });
        }
    };

    loadData = async () => {
        const { rosterStore, scheduleStore, commonStore, userStore } = this.props;
        const dateTimeRules = commonStore.getDateTimeRules();
        const { Manages = [] } = userStore.currentUser;
        const { availableDepartment: departmentOptions } = await rosterStore.getRuleBasedDepartments({ rule: 'roster_template', status: 'published' });
        const { rosterTemplates: publishedTemplates } = await rosterStore.getPublishedRosterTemplates({ department: departmentOptions && departmentOptions[0]?.value, users: Manages });
        const resourceResponse = await scheduleStore.fetchResourcesOnly({ pmMembers: Manages.join(',') });
        const _resources = (resourceResponse.resources || []).slice(1);
        const usersOptions = _resources.map((user) => {
            return {
                label: user.name,
                value: user.id,
                disabled: true,
                ...user
            }
        });

        const templateOptions = Array.from(new Map(publishedTemplates.map((tpl) => [
            tpl.roster_template_id,
            {
                value: tpl.roster_template_id,
                label: `${tpl?.Roster_Template?.name}: (${shortDateFormat(tpl.cycle_start_date, dateTimeRules)} - ${shortDateFormat(tpl.cycle_end_date, dateTimeRules)})`,
                ...tpl
            },])).values()
        );

        this.setState(
            {
                usersOptions,
                publishedTemplates,
                isLoading: false,
                departmentOptions,
                department: departmentOptions[0]?.value,
                templateOptions,
                selectedTemplate: templateOptions[0]?.value,
                start_date: publishedTemplates[0]?.cycle_start_date,
                end_date: publishedTemplates[0]?.cycle_end_date,
            },
            this.initRosterScheduler
        );
    };

    initRosterScheduler = () => {
        const { t } = this.props;
        const { selectedUsers, publishedTemplates } = this.state;
        const events = generateEventsFromRosterTemplates(publishedTemplates);
        const schedulerData = new SchedulerData(
            moment().format('YYYY-MM-DD'),
            ViewTypes.Custom1,
            false,
            false,
            {
                headerEnabled: false,
                startResizable: false,
                endResizable: false,
                movable: false,
                creatable: false,
                resourceName: t('Employees name'),
                nonAgendaOtherCellHeaderFormat: 'ddd | D',
                schedulerWidth: '78%',
                tableHeaderHeight: 75,
                eventItemHeight: 40,
                eventItemLineHeight: 40,
                yearCellWidth: 60,
            },
            { getCustomDateFunc: this.getCustomDate }
        );

        schedulerData.localeMoment.locale('en');
        schedulerData.setResources(selectedUsers);
        schedulerData.setEvents(events);
        this.setState({ schedulerData, events });
    };

    getCustomDate = (schedulerData, num, date) => {
        const { viewType, startDate, localeMoment } = schedulerData;
        let selectDate = date || startDate;
        const { start_date, end_date } = this.state;

        if (viewType === ViewTypes.Custom1) {
            return {
                startDate: moment(start_date).format(DATE_FORMAT),
                endDate: moment(end_date).format(DATE_FORMAT),
                cellUnit: CellUnits.Day,
            };
        }

        if (viewType === ViewTypes.Custom2) {
            const firstDay = localeMoment(selectDate).startOf('month').format(DATE_FORMAT);
            const startDateStr =
                num === 0 ? firstDay : localeMoment(firstDay).add(2 * num, 'months').format(DATE_FORMAT);
            return {
                startDate: startDateStr,
                endDate: localeMoment(startDateStr).add(1, 'months').endOf('month').format(DATE_FORMAT),
                cellUnit: CellUnits.Day,
            };
        }

        const startDateStr = num === 0 ? selectDate : localeMoment(selectDate).add(2 * num, 'days').format(DATE_FORMAT);
        return {
            startDate: startDateStr,
            endDate: localeMoment(startDateStr).add(1, 'days').format(DATE_FORMAT),
            cellUnit: CellUnits.Hour,
        };
    };

    getAvailableUsersWithinDates = (usersOptions, publishedTemplates, startDateStr, endDateStr, projectId = null, department) => {
        const { t } = this.props;
        const newStart = new Date(startDateStr);
        const newEnd = new Date(endDateStr);

        const overlappingIds = new Set();
        publishedTemplates.forEach((template) => {
            const templateStart = new Date(template.cycle_start_date);
            const templateEnd = new Date(template.cycle_end_date);
            if (templateStart <= newEnd && templateEnd >= newStart) {
                overlappingIds.add(template.user_id);
            }
        });

        return usersOptions.filter((user) => user.employee_type === department)
            .map((user) => ({
                ...user,
                _label: !overlappingIds.has(user.value) ? t('No Templates Found') : '',
                disabled: !overlappingIds.has(user.value),
            }));
    };

    handleChange = async (name, value) => {
        const { rosterStore, commonStore, userStore, taskStore, userGroupStore } = this.props;
        const { Manages = [] } = userStore.currentUser;
        let { start_date, end_date, project_id, usersOptions, publishedTemplates, department, templateOptions, taskMembers } = this.state;
        if (name === 'start_date' || name === 'end_date') {
            if (name === 'start_date') start_date = value;
            else end_date = value;

            if (moment(end_date).isBefore(start_date)) {
                if (name === 'start_date') end_date = start_date;
                else start_date = end_date;
            }
            const { rosterTemplates: _publishedTemplates } = await rosterStore.getPublishedRosterTemplates({ department, users: Manages });

            const updatedOptions = this.getAvailableUsersWithinDates(taskMembers, _publishedTemplates, start_date, end_date, project_id, department);

            this.setState(
                {
                    start_date,
                    end_date,
                    publishedTemplates: _publishedTemplates,
                    userOptionsFilter: updatedOptions,
                    selectedUsers: [],
                },
                this.initRosterScheduler
            );
            return;
        }
        else if (name === 'task_id') {
            if (!value || !parseInt(value)) {
                this.setState({ task_id: null, userOptionsFilter: [], selectedUsers: [] }, this.initRosterScheduler);
                return;
            }
            const { rows } = await taskStore.loadMembersForTask(value, true);
            const taskMembers = rows.map((member) => member.user_id);
            const filteredUsersOptions = usersOptions.filter((user) => taskMembers.includes(user.value));
            const updatedOptions = this.getAvailableUsersWithinDates(filteredUsersOptions, publishedTemplates, start_date, end_date, project_id, department);
            this.setState({ task_id: value, userOptionsFilter: updatedOptions, selectedUsers: [], taskMembers: filteredUsersOptions }, this.initRosterScheduler);
            return;
        }
        else if (name === 'user_group') {
            const updatedOptions = this.getAvailableUsersWithinDates(taskMembers, publishedTemplates, start_date, end_date, project_id, department);
            if (value > 0) {
                const { user_groups } = await userGroupStore.getUsersInGroup(value);
                const userIds = new Set(user_groups.map(row => row.user_id));

                const usersByGroup = updatedOptions.filter((user) => userIds.has(user.id));
                this.setState({ user_group: value, userOptionsFilter: usersByGroup, selectedUsers: [] }, this.initRosterScheduler);
            } else {
                this.setState({ user_group: value, userOptionsFilter: updatedOptions, selectedUsers: [] }, this.initRosterScheduler);
            }
            return;
        }
        else if (name === 'department') {
            const dateTimeRules = commonStore.getDateTimeRules();
            const { rosterTemplates: _publishedTemplates } = await rosterStore.getPublishedRosterTemplates({ department: value, users: Manages });
            const updatedOptions = this.getAvailableUsersWithinDates(taskMembers, _publishedTemplates, start_date, end_date, project_id, value);

            const templateOptions = Array.from(new Map(_publishedTemplates.map((tpl) => [
                tpl.roster_template_id,
                {
                    value: tpl.roster_template_id,
                    label: `${tpl?.Roster_Template?.name}: (${shortDateFormat(tpl.cycle_start_date, dateTimeRules)} - ${shortDateFormat(tpl.cycle_end_date, dateTimeRules)})`,
                    ...tpl
                },])).values()
            );

            this.setState(
                {
                    publishedTemplates: _publishedTemplates,
                    userOptionsFilter: updatedOptions,
                    selectedUsers: [],
                    department: value,
                    templateOptions,
                    selectedTemplate: templateOptions[0]?.value,
                    start_date: _publishedTemplates[0]?.cycle_start_date,
                    end_date: _publishedTemplates[0]?.cycle_end_date,
                    user_group: null,
                },
                this.initRosterScheduler
            );
            return;
        }
        else if (name === 'selectedTemplate') {
            const selectedTemplate = templateOptions.find(ele => ele.roster_template_id === value)

            this.setState(
                {
                    selectedUsers: [],
                    templateOptions,
                    selectedTemplate: value,
                    start_date: selectedTemplate?.cycle_start_date,
                    end_date: selectedTemplate?.cycle_end_date,
                },
                this.initRosterScheduler
            );
            return;
        }

        this.setState({ [name]: value });
    };

    handleUserSelection = (selectedIds) => {
        const { userOptionsFilter } = this.state;
        const selectedUsers = userOptionsFilter.filter((user) => selectedIds.includes(user.value));
        this.setState({ selectedUsers }, this.initRosterScheduler);
    };

    handleAutoTimesheetDaysChange = (autoTimesheetDays) => {
        this.setState({ autoTimesheetDays });
    };

    handleOverrideChange = ({ overrideShift, overrideAutoTimeRecording }) => {
        this.setState({ overrideShift, overrideAutoTimeRecording });
    };

    handleSave = async () => {
        const { start_date, end_date, project_id, task_id, selectedUsers, user_group, autoTimesheetDays, overrideShift, overrideAutoTimeRecording } = this.state;
        const { commonStore, scheduleStore, handleClose, t } = this.props;
        if (!selectedUsers.length) {
            commonStore.addNotification(t('Please select employee to assign shift'), null, 'warning');
            return;
        }
        const allowUserAutoTimesheetToggle = !!commonStore?.config?.client?.data?.loginRules?.allowUserToEnableDisableAutoTimesheet;
        const disabledAutoTimesheetUserIds = allowUserAutoTimesheetToggle
            ? selectedUsers.filter(u => u.allow_user_for_auto_timesheet === false).map(u => u.value)
            : [];
        const data = {
            start_date: moment(start_date).format('YYYY-MM-DD'),
            end_date: moment(end_date).format('YYYY-MM-DD'),
            project_id: parseInt(project_id),
            task_id: parseInt(task_id),
            assignees: selectedUsers.map((user) => user.value),
            user_group,
            autoTimesheetDays,
            disabledAutoTimesheetUserIds,
            overrideShift,
            overrideAutoTimeRecording,
        };

        try {
            const res = await scheduleStore.addRosterShift(data);
            if (res?.shiftsFailedToCreate.length) {
                if (res.draftShiftRecords?.length) {
                    commonStore.addNotification(t('Bulk shifts created, however some shifts failed to create please check'), null, 'warning');
                } else {
                    commonStore.addNotification(t('Failed to create shift due to intersections as shown below'), null, 'error');
                }
                this.setState({ shiftsFailedToCreate: res.shiftsFailedToCreate });
            } else if (res.draftShiftRecords?.length) {
                commonStore.addNotification(t('Roster shifts created successfully'), null, 'success');
                this.setState({ shiftsCreatedSuccessfully: { shifts: res.draftShiftRecords, usersMap: res.usersMap, shiftTask: res.shiftTask } });
            } else {
                commonStore.addNotification(t('Roster shifts created successfully'), null, 'success');
                handleClose();
            }
        } catch (error) {
            commonStore.addNotification(t(error.message || 'Error'), null, 'error');
            console.error(error);
        }
    };

    customEventItemTemplate = (_, eventItem) => (
        <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: 'black' }}>
            <div style={{ fontSize: 12 }}>{eventItem.title}</div>
        </div>
    );

    render() {
        const { show, handleClose, t, commonStore, userStore } = this.props;
        const { isLoading, start_date, end_date, project_id, task_id, userOptionsFilter, selectedUsers, user_group,
            width, shiftsFailedToCreate, departmentOptions, department, templateOptions, selectedTemplate, schedulerData,
            shiftsCreatedSuccessfully, autoTimesheetDays, overrideShift, overrideAutoTimeRecording } = this.state;
        const dateTimeRules = commonStore.getDateTimeRules();

        return (
            <Modal className={`${shiftsCreatedSuccessfully ? '' : 'modal right fade modal_revised'}`} size="xl" centered={shiftsCreatedSuccessfully ? false : true} show={show} onHide={handleClose} dialogClassName={`${shiftsCreatedSuccessfully ? 'lg bulk-shift-modal' : ''}`}>
                <Modal.Header className="set_ModalHeader">
                    <Modal.Title className="w-100 text-center custom-modal-title">
                        {t('Publish Shifts According to Roster')}
                    </Modal.Title>
                    <img src={CloseModalIcon} className="cursor-pointer" onClick={handleClose} alt="close-btn" />
                </Modal.Header>

                <Modal.Body>
                    {isLoading ? (
                        <LoadingSpinner />
                    ) : (
                        <div>
                            {shiftsCreatedSuccessfully ? (
                                <BulkShiftSuccessSummary
                                    data={shiftsCreatedSuccessfully}
                                    dateTimeRules={dateTimeRules}
                                    onClose={handleClose}
                                    t={t}
                                    className="mt-2"
                                />
                            ) : !shiftsFailedToCreate ? (
                                <div>
                                    <Row className="mt-2">
                                        <Col xs={2}>
                                            <label className="control-label mb-1 fs15">{t('Department')}</label>
                                            <Select
                                                placeholder={t('Select...')}
                                                options={departmentOptions}
                                                value={departmentOptions.find((opt) => opt.value === department)}
                                                onChange={(e) => this.handleChange('department', e?.value)}
                                            />
                                        </Col>
                                        <Col md={4}>
                                            <label className="control-label mb-1">{t('Select Template')}</label>
                                            <Select
                                                options={templateOptions}
                                                value={templateOptions.find((opt) => opt.value === selectedTemplate)}
                                                onChange={(e) => this.handleChange('selectedTemplate', e.value)}
                                                placeholder={t('Select...')}
                                            />
                                            <i className='fs12 color-2550AC'>{t('Selecting a template will automatically select the date. It does not apply any filters')}</i>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md={6}>
                                            <label className="control-label">{t('From')}</label>
                                            <DatePicker
                                                selected={new Date(start_date)}
                                                onChange={(date) => this.handleChange('start_date', date.toString())}
                                                dateFormat={longDateFormat(dateTimeRules)}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <label className="control-label">{t('To')}</label>
                                            <DatePicker
                                                selected={new Date(end_date)}
                                                onChange={(date) => this.handleChange('end_date', date.toString())}
                                                dateFormat={longDateFormat(dateTimeRules)}
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="mt-2">
                                        <Col md={6}>
                                            <label className="control-label mb-1">{t('Project')}</label>
                                            <ProjectsWidget
                                                value={project_id || null}
                                                onChange={(value) => this.handleChange('project_id', value)}
                                                fromShift={userStore.currentUser.user_type === 'pm' ? true : false}

                                            />
                                        </Col>
                                        <Col md={6}>
                                            <label className="control-label mb-1">{t('Task')}</label>
                                            <TasksWidget
                                                value={task_id || null}
                                                onChange={(value) => this.handleChange('task_id', value)}
                                                filterByProject={project_id}
                                                defaultIfOne
                                            />
                                        </Col>
                                    </Row>

                                    <Row className="mt-2">
                                        <Col md={6}>
                                            <label className="control-label mb-1">{t('Group')}</label>
                                            <CurrentUserGroupsWidget
                                                value={user_group}
                                                onChange={(value) => this.handleChange('user_group', value)}
                                            />
                                        </Col>
                                        <Col md={6}>
                                            <label className="control-label mb-1">{t('Employee')}</label>
                                            <CustomMultiSelect
                                                options={userOptionsFilter || []}
                                                value={selectedUsers.map((u) => u.value) || []}
                                                onChange={this.handleUserSelection}
                                                customOptions
                                            />
                                            <i className='fs12 color-2550AC'>{t('Please select the project and task first')}</i>
                                        </Col>
                                    </Row>

                                    <AutoTimeRecordingDays
                                        autoTimesheetDays={autoTimesheetDays}
                                        onChange={this.handleAutoTimesheetDaysChange}
                                        selectedUsers={selectedUsers}
                                    />
                                    <RosterOverrideSettings
                                        overrideShift={overrideShift}
                                        overrideAutoTimeRecording={overrideAutoTimeRecording}
                                        onChange={this.handleOverrideChange}
                                    />

                                    {schedulerData && (
                                        <div ref={this.scheduleRef} id="Roster-Scheduler" style={{ marginTop: 15 }}>
                                            {selectedUsers.length > 0 && (
                                                <>
                                                    <div className="my-1">{t('Selected Shifts')}</div>
                                                    <Scheduler
                                                        width={width}
                                                        schedulerData={schedulerData}
                                                        eventItemTemplateResolver={this.customEventItemTemplate}
                                                        dateTimeRules={dateTimeRules}
                                                        t={t}
                                                    />
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className="mt-3">
                                    <AfterBulkCreateFailedResponseModalBody
                                        shiftsFailedToCreate={shiftsFailedToCreate}
                                        dateTimeRules={dateTimeRules}
                                        t={t}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                </Modal.Body>

                {!shiftsFailedToCreate?.length && !shiftsCreatedSuccessfully &&
                    <Modal.Footer>
                        <Button onClick={handleClose}>{t('Close')}</Button>
                        <Button fill md onClick={this.handleSave}>{t('Save')}</Button>
                    </Modal.Footer>
                }
            </Modal>
        );
    }
}

export default inject('rosterStore', 'scheduleStore', 'commonStore', 'userStore', 'taskStore', 'userGroupStore')(
    applicationRouter(withLocalization(observer(RosterShiftsModal)))
);
